/**
 * useReceptionExport Hook
 * Export functionality for reception reports
 *
 * @version 2025.1.0
 */

import { useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { ReceptionFilters } from '../types/reception';
import receptionService from '../services/receptionService';

export type ExportFormat = 'xlsx' | 'csv';

interface UseReceptionExportReturn {
  exporting: boolean;
  exportReport: (filters: ReceptionFilters, format?: ExportFormat, filename?: string) => Promise<boolean>;
}

export function useReceptionExport(): UseReceptionExportReturn {
  const { enqueueSnackbar } = useSnackbar();
  const [exporting, setExporting] = useState(false);

  const exportReport = useCallback(
    async (
      filters: ReceptionFilters,
      format: ExportFormat = 'xlsx',
      filename?: string
    ): Promise<boolean> => {
      setExporting(true);

      try {
        const blob = await receptionService.exportReport(filters, format);

        // Generate filename if not provided
        const date = filters.date || new Date().toISOString().split('T')[0];
        const extension = format === 'csv' ? 'csv' : 'xlsx';
        const defaultFilename = `recepcao-${date}.${extension}`;
        const downloadFilename = filename || defaultFilename;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', downloadFilename);
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        enqueueSnackbar('Relatório exportado com sucesso', { variant: 'success' });
        return true;
      } catch (error) {
        console.error('Error exporting reception report:', error);
        enqueueSnackbar('Erro ao exportar relatório', { variant: 'error' });
        return false;
      } finally {
        setExporting(false);
      }
    },
    [enqueueSnackbar]
  );

  return {
    exporting,
    exportReport,
  };
}

export default useReceptionExport;
